<template>
  <div class="pb-10">
    <Selector
      :isEditMode="isEditMode"
      :hasAttachments="
        filesToUpload.length > 0 || measureAttachments.length > 0
      "
      @fileSelected="handleFileSelected"
    />

    <AttachmentCard
      v-for="file in filesToUpload"
      :key="file.id"
      :filename="file.name"
      :isUploading="file.isUploading"
    />

    <AttachmentCard
      v-for="attachment in measureAttachments"
      :key="attachment.id"
      :filename="attachment.media.originalFileName"
      :isEditMode="isEditMode"
      @download="downloadFile(attachment)"
      @delete="showDeleteConfirmation(attachment)"
    />

    <DeleteConfirmationModal
      :show="deleteConfirmationVisible"
      :isLoading="isDeleting"
      :title="$t('problem_solving.measure.delete_attachment')"
      :text="
        $t('problem_solving.measure.delete_attachment_description', {
          filename: selectedAttachmentFilename,
        })
      "
      @cancel="cancelDelete"
      @delete="handleDeleteConfirmation"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'

import MediaSDK from '@/lib/ReverMediaSDK'
import mixin from '@/mixins/mixin.vue'

import Selector from './AttachmentSelector'
import AttachmentCard from './AttachmentCard'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

export default {
  mixins: [mixin],

  props: {
    rev: {
      type: Object,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      required: false,
    },
  },

  components: {
    Selector,
    AttachmentCard,
    DeleteConfirmationModal,
  },

  data() {
    return {
      filesToUpload: [],
      deleteConfirmationVisible: false,
      selectedAttachment: null,
      isDeleting: false,
    }
  },

  computed: {
    ...mapState('attachments', {
      attachments(state) {
        return state.attachments
      },
    }),

    measureAttachments() {
      const ids = this.rev?.problemAnalysis?.measureAttachments ?? []
      return this.attachments.filter(a => ids.includes(a?.media?.id))
    },

    selectedAttachmentFilename() {
      return this.selectedAttachment?.media?.originalFileName ?? ''
    },
  },

  methods: {
    ...mapActions({
      getAttachments: 'attachments/get',
      createAttachment: 'attachments/create',
      deleteAttachment: 'attachments/delete',
    }),

    ...mapMutations({
      updateMeasureAttachments: 'problemSolving/updateMeasureAttachments',
    }),

    handleFileSelected(file) {
      file.isUploading = true
      this.filesToUpload.unshift(file)
      this.uploadFile(file)
    },

    async uploadFile(file) {
      try {
        const mediaObject = await MediaSDK.uploadMedia(file.file)

        await this.createAttachment({
          organizationId: this.rev.organization,
          revId: this.rev.id,
          revType: 'problem-solving',
          mediaId: mediaObject.id,
        })

        await this.getAttachments({
          organizationId: this.rev.organization,
          revId: this.rev.id,
          revType: 'problem-solving',
        })

        this.updateMeasureAttachments([
          mediaObject.id,
          ...(this.rev?.problemAnalysis?.measureAttachments ?? []),
        ])
      } catch (err) {
        this.showError(this.$t('attachments.uploading_the_file_failed'))
      } finally {
        this.filesToUpload = this.filesToUpload.filter(f => f.id !== file.id)
      }
    },

    downloadFile(attachment) {
      MediaSDK.download(attachment.media)
    },

    showDeleteConfirmation(attachment) {
      this.selectedAttachment = attachment
      this.deleteConfirmationVisible = true
    },

    cancelDelete() {
      this.selectedAttachment = null
      this.deleteConfirmationVisible = false
    },

    async handleDeleteConfirmation() {
      try {
        this.isDeleting = true

        await this.deleteAttachment({
          organizationId: this.rev.organization,
          attachmentId: this.selectedAttachment.id,
        })

        await this.getAttachments({
          organizationId: this.rev.organization,
          revId: this.rev.id,
          revType: 'problem-solving',
        })

        // problemAnalysis.measureAttachments are actually media ids instead of attachment ids, that's why filtering is applied based on the media object id and not the attachment's id
        const measureAttachments =
          this.rev?.problemAnalysis?.measureAttachments?.filter?.(
            id => id !== this.selectedAttachment.media.id
          ) ?? []

        this.updateMeasureAttachments(measureAttachments)

        this.selectedAttachment = null
        this.deleteConfirmationVisible = false
      } catch (err) {
        this.showError(
          this.$t(
            'attachments.there_was_a_problem_deleting_this_attachment_try_again'
          )
        )
      } finally {
        this.isDeleting = false
      }
    },
  },
}
</script>
